import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import {
    LogOut, Calendar, Users, Settings, Home, User, BookOpen, List,
    Clock, FileText, Menu, X, FileEdit, Library, PenTool, CreditCard, Video
} from 'lucide-react';
import { formatUserDisplayName } from '../utils/userDisplayName';
import './Sidebar.css';

const Sidebar = () => {
    const { user, logout } = useAuth();
    const { config } = useConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(()=>window.innerWidth>768);

    useEffect(()=>{document.documentElement.dataset.sidebar=isOpen?'open':'closed';return()=>{delete document.documentElement.dataset.sidebar;};},[isOpen]);
    useEffect(()=>{if(window.innerWidth<=768)setIsOpen(false);},[location.pathname]);
    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    return (
        <>
            {!isOpen&&<button className="sidebar-reopen" aria-label="Открыть меню" aria-expanded="false" onClick={()=>setIsOpen(true)}><Menu size={22}/></button>}
            <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
                <div className="sidebar-header">
                    <Link to="/" className="sidebar-logo">
                        <Calendar size={24} />
                        {isOpen && <span>Календарь</span>}
                    </Link>
                    <button
                        className="sidebar-toggle"
                        aria-label={isOpen?"Свернуть меню":"Открыть меню"}
                        aria-expanded={isOpen}
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        {isOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {/* Видеовстречи временно скрыты
                    <Link to="/meet" className={`sidebar-nav-item ${isActive('/meet') ? 'active' : ''}`} title="Видеовстречи">
                        <Video size={20} />
                        {isOpen && <span>Видеовстречи</span>}
                    </Link>
                    */}
                    {/* Инструкция - для преподавателей и админов */}
                    {(user?.is_teacher || user?.role === 'admin') && (
                        <Link
                            to="/instructions"
                            className={`sidebar-nav-item ${isActive('/instructions') ? 'active' : ''}`}
                            title="Инструкция"
                        >
                            <BookOpen size={20} />
                            {isOpen && <span>Инструкция</span>}
                        </Link>
                    )}

                    {/* База знаний - только MAG, для преподавателей и админов */}
                    {config?.code === 'mag' && (user?.is_teacher || user?.role === 'admin') && (
                        <Link
                            to="/knowledge"
                            className={`sidebar-nav-item ${isActive('/knowledge') && !isActive('/knowledge/editor') ? 'active' : ''}`}
                            title="База знаний"
                        >
                            <Library size={20} />
                            {isOpen && <span>База знаний</span>}
                        </Link>
                    )}

                    {/* Редактор знаний - только MAG, для редакторов и админов */}
                    {config?.code === 'mag' && (user?.is_knowledge_editor || user?.role === 'admin') && (
                        <Link
                            to="/knowledge/editor"
                            className={`sidebar-nav-item ${isActive('/knowledge/editor') ? 'active' : ''}`}
                            title="Редактор знаний"
                        >
                            <PenTool size={20} />
                            {isOpen && <span>Редактор знаний</span>}
                        </Link>
                    )}

                    {/* Группа 1: Группы, Календарь, Доступные группы — только если есть хотя бы один пункт */}
                    {(user?.is_teacher || user?.role === 'admin') && (
                        <div className="sidebar-nav-group sidebar-nav-group-blue">
                            <Link
                                to="/"
                                className={`sidebar-nav-item ${isActive('/') && !isActive('/calendar') && !isActive('/admin') && !isActive('/profile') && !isActive('/therapist') && !isActive('/instructions') && !isActive('/articles') && !isActive('/public/articles') && !isActive('/knowledge') && !isActive('/zoom') ? 'active' : ''}`}
                                title="Группы"
                            >
                                <Home size={20} />
                                {isOpen && <span>Группы</span>}
                            </Link>
                            <Link
                                to="/calendar"
                                className={`sidebar-nav-item ${isActive('/calendar') ? 'active' : ''}`}
                                title="Календарь"
                            >
                                <Calendar size={20} />
                                {isOpen && <span>Календарь</span>}
                            </Link>
                            <a
                                href="/public/groups"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`sidebar-nav-item ${isActive('/public/groups') ? 'active' : ''}`}
                                title="Доступные группы"
                            >
                                <List size={20} />
                                {isOpen && <span>Доступные группы</span>}
                            </a>
                            {config?.code === 'mag' && (
                                <a
                                    href="https://reports.mag-gestalt.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sidebar-nav-item sidebar-nav-reports"
                                    title="Отчёты"
                                >
                                    <FileText size={20} />
                                    {isOpen && <span>Отчёты</span>}
                                </a>
                            )}
                        </div>
                    )}

                    {/* Группа 2: Расписание, Консультации, Психологи */}
                    {(user?.is_therapist || user?.role === 'admin') && (
                        <div className="sidebar-nav-group sidebar-nav-group-green">
                            {(user?.is_therapist || user?.role === 'admin') && (
                                <>
                                    <Link
                                        to="/therapist/schedule"
                                        className={`sidebar-nav-item ${isActive('/therapist/schedule') ? 'active' : ''}`}
                                        title="Расписание"
                                    >
                                        <Clock size={20} />
                                        {isOpen && <span>Расписание</span>}
                                    </Link>
                                    <Link
                                        to="/therapist/bookings"
                                        className={`sidebar-nav-item ${isActive('/therapist/bookings') ? 'active' : ''}`}
                                        title="Консультации"
                                    >
                                        <FileText size={20} />
                                        {isOpen && <span>Консультации</span>}
                                    </Link>
                            {user?.is_therapist && config?.code === 'mag' && (
                                <Link
                                    to="/therapist/payments"
                                    className={`sidebar-nav-item ${isActive('/therapist/payments') ? 'active' : ''}`}
                                    title="Мои платежи"
                                >
                                    <CreditCard size={20} />
                                    {isOpen && <span>Мои платежи</span>}
                                </Link>
                            )}
                                </>
                            )}
                            {(user?.is_therapist || user?.role === 'admin') && (
                                <Link
                                    to={config?.code === 'mag' ? (config.agregatorUrl || 'https://test.mag-gestalt.com') : '/public/therapists'}
                                    className={`sidebar-nav-item ${isActive('/public/therapists') ? 'active' : ''}`}
                                    title={config?.code === 'mag' ? 'Магрегатор' : config?.code === 'nasledie' ? 'Наши психологи' : 'Психологи'}
                                >
                                    <Users size={20} />
                                    {isOpen && <span>{config?.code === 'mag' ? 'Магрегатор' : config?.code === 'nasledie' ? 'Наши психологи' : 'Психологи'}</span>}
                                </Link>
                            )}
                            {user?.is_therapist && (
                                <Link
                                    to={config?.code === 'mag' ? `${config.agregatorUrl || 'https://test.mag-gestalt.com'}/specialists/${user.id}` : `/public/profile/${user.id}`}
                                    className={`sidebar-nav-item ${isActive(`/public/profile/${user.id}`) ? 'active' : ''}`}
                                    title="Мой профиль"
                                >
                                    <User size={20} />
                                    {isOpen && <span>Мой профиль</span>}
                                </Link>
                            )}

                        </div>
                    )}

                    {/* Статьи — только Наследие */}
                    {config?.code === 'nasledie' && (
                        <>
                            <div className="sidebar-nav-spacer" />
                            <Link
                                to="/zoom"
                                className={`sidebar-nav-item ${isActive('/zoom') ? 'active' : ''}`}
                                title="Zoom"
                            >
                                <Video size={20} />
                                {isOpen && <span>Zoom</span>}
                            </Link>
                            <Link
                                to="/public/articles"
                                className={`sidebar-nav-item ${isActive('/public/articles') || (isActive('/articles') && !isActive('/articles/editor')) ? 'active' : ''}`}
                                title="Статьи"
                            >
                                <FileEdit size={20} />
                                {isOpen && <span>Статьи</span>}
                            </Link>
                            <Link
                                to="/articles/editor"
                                className={`sidebar-nav-item ${isActive('/articles/editor') ? 'active' : ''}`}
                                title="Редактор статей"
                            >
                                <PenTool size={20} />
                                {isOpen && <span>Редактор статей</span>}
                            </Link>
                        </>
                    )}
                    {config?.code !== 'nasledie' && user?.role === 'admin' && <div className="sidebar-nav-spacer" />}

                    {/* Админка - только для админов (в самом низу) */}
                    {user?.role === 'admin' && (
                        <Link
                            to="/admin"
                            className={`sidebar-nav-item sidebar-nav-admin ${isActive('/admin') ? 'active' : ''}`}
                            title="Админ"
                        >
                            <Settings size={20} />
                            {isOpen && <span>Админ</span>}
                        </Link>
                    )}
                </nav>

                <div className="sidebar-footer">
                    <Link
                        to="/profile"
                        className={`sidebar-nav-item ${isActive('/profile') ? 'active' : ''}`}
                        title="Профиль"
                    >
                        {user?.photo_url ? (
                            <img src={user.photo_url} alt={formatUserDisplayName(user)} className="sidebar-user-avatar" />
                        ) : (
                            <div className="sidebar-user-avatar-placeholder">
                                <User size={16} />
                            </div>
                        )}
                        {isOpen && <span>{formatUserDisplayName(user)}</span>}
                    </Link>
                    <button
                        onClick={handleLogout}
                        className="sidebar-nav-item sidebar-logout"
                        title="Выйти"
                    >
                        <LogOut size={20} />
                        {isOpen && <span>Выйти</span>}
                    </button>
                </div>
            </div>
            {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}
        </>
    );
};

export default Sidebar;
